import { getOrderByCode } from 'services/api/pay';
import { logger } from 'services/logger';
import { getCurrentAsaasIdentityKey } from './identity';
import { clearPendingAsaasPayment, findPendingAsaasPaymentByOrderCode } from './pending';
import {
  ASAAS_MAX_QUERY_FAILURES,
  ASAAS_POLL_INTERVAL_MS,
  ASAAS_POLL_WINDOW_MS,
  ASAAS_PAYMENT_STATUS,
  type AsaasPaymentSnapshot,
  type AsaasPaymentStatus,
  type AsaasPaymentSource,
  type AsaasPaymentWatcher,
} from './types';

interface InternalWatcher {
  orderCode: string;
  identityKey?: string;
  snapshot: AsaasPaymentSnapshot;
  listeners: Set<(snapshot: AsaasPaymentSnapshot) => void>;
  startedAt: number;
  failures: number;
  timer?: ReturnType<typeof setTimeout>;
  inFlight: boolean;
  pendingCheck: boolean;
  generation: number;
  disposed: boolean;
}

interface AsaasPaymentHandlers {
  refresh(orderCode: string, source?: AsaasPaymentSource): void;
  announce(orderCode: string, source?: AsaasPaymentSource): void;
}

const watchers = new Map<string, AsaasPaymentWatcher>();
const internalWatchers = new Map<string, InternalWatcher>();
const panelCounts = new Map<string, number>();
const panelSources = new Map<string, AsaasPaymentSource>();
const refreshedOrders = new Set<string>();
const announcedOrders = new Set<string>();

let handlers: AsaasPaymentHandlers = {
  refresh: () => {},
  announce: () => {},
};

export function registerAsaasPaymentHandlers(next: AsaasPaymentHandlers): void {
  handlers = next;
}

export function markAsaasResultPanelActive(
  orderCode: string,
  active: boolean,
  source?: AsaasPaymentSource,
): void {
  const next = (panelCounts.get(orderCode) ?? 0) + (active ? 1 : -1);
  if (next <= 0) {
    panelCounts.delete(orderCode);
    panelSources.delete(orderCode);
  } else {
    panelCounts.set(orderCode, next);
    if (source) panelSources.set(orderCode, source);
  }
}

export function getAsaasPanelSource(orderCode: string): AsaasPaymentSource | undefined {
  return panelSources.get(orderCode);
}

function emit(watcher: InternalWatcher): void {
  const snapshot = { ...watcher.snapshot };
  for (const listener of [...watcher.listeners]) listener(snapshot);
}

function applySnapshot(watcher: InternalWatcher, patch: Partial<AsaasPaymentSnapshot>): void {
  watcher.snapshot = { ...watcher.snapshot, ...patch };
  emit(watcher);
}

function stopPolling(watcher: InternalWatcher): void {
  if (watcher.timer !== undefined) {
    clearTimeout(watcher.timer);
    watcher.timer = undefined;
  }
}

function disposeWatcher(watcher: InternalWatcher): void {
  watcher.disposed = true;
  stopPolling(watcher);
  watcher.listeners.clear();
  internalWatchers.delete(watcher.orderCode);
  watchers.delete(watcher.orderCode);
}

function identityChanged(watcher: InternalWatcher): boolean {
  return getCurrentAsaasIdentityKey() !== watcher.identityKey;
}

function stopIfIdentityChanged(watcher: InternalWatcher): boolean {
  if (!identityChanged(watcher)) return false;
  disposeWatcher(watcher);
  return true;
}

function scheduleNext(watcher: InternalWatcher): void {
  stopPolling(watcher);
  if (watcher.disposed) return;
  watcher.timer = setTimeout(() => {
    watcher.timer = undefined;
    runQuery(watcher);
  }, ASAAS_POLL_INTERVAL_MS);
}

function isOrderInaccessible(error: unknown): boolean {
  const value = error as { response?: { status?: number }; data?: { status?: number } };
  return value?.response?.status === 404 || value?.data?.status === 404;
}

function applyStatus(
  watcher: InternalWatcher,
  status: { paymentStatus: AsaasPaymentStatus },
): void {
  if (status.paymentStatus === ASAAS_PAYMENT_STATUS.SUCCEEDED) {
    stopPolling(watcher);
    const pending = findPendingAsaasPaymentByOrderCode(watcher.orderCode);
    const source = pending?.source ?? panelSources.get(watcher.orderCode);
    clearPendingAsaasPayment(watcher.orderCode);
    applySnapshot(watcher, {
      phase: 'succeeded',
      polling: false,
      retryable: false,
      paymentStatus: ASAAS_PAYMENT_STATUS.SUCCEEDED,
      error: undefined,
    });
    if (!refreshedOrders.has(watcher.orderCode)) {
      refreshedOrders.add(watcher.orderCode);
      try {
        handlers.refresh(watcher.orderCode, source);
      } catch (error) {
        logger.error(`[asaas][refresh_failed] ${String(error)}`);
      }
    }
    if (!panelCounts.has(watcher.orderCode) && !announcedOrders.has(watcher.orderCode)) {
      announcedOrders.add(watcher.orderCode);
      handlers.announce(watcher.orderCode, source);
    }
    return;
  }

  if (
    status.paymentStatus === ASAAS_PAYMENT_STATUS.FAILED ||
    status.paymentStatus === ASAAS_PAYMENT_STATUS.CANCELLED
  ) {
    stopPolling(watcher);
    clearPendingAsaasPayment(watcher.orderCode);
    applySnapshot(watcher, {
      phase: status.paymentStatus === ASAAS_PAYMENT_STATUS.FAILED ? 'failed' : 'cancelled',
      polling: false,
      retryable: false,
      paymentStatus: status.paymentStatus,
      error: undefined,
    });
    return;
  }

  watcher.failures = 0;
  if (Date.now() - watcher.startedAt >= ASAAS_POLL_WINDOW_MS) {
    stopPolling(watcher);
    applySnapshot(watcher, {
      phase: 'confirming',
      polling: false,
      retryable: true,
      paymentStatus: ASAAS_PAYMENT_STATUS.PENDING,
      error: undefined,
    });
    return;
  }
  applySnapshot(watcher, {
    phase: 'pending',
    polling: true,
    retryable: true,
    paymentStatus: ASAAS_PAYMENT_STATUS.PENDING,
    error: undefined,
  });
  scheduleNext(watcher);
}

function applyQueryError(watcher: InternalWatcher, error: unknown): void {
  const queryError = error instanceof Error ? error : new Error(String(error));
  logger.error(`[asaas][status_failed] ${queryError.message}`);
  if (isOrderInaccessible(error)) {
    stopPolling(watcher);
    applySnapshot(watcher, {
      phase: 'unrecoverable',
      polling: false,
      retryable: false,
      error: queryError,
    });
    return;
  }
  watcher.failures += 1;
  if (watcher.failures >= ASAAS_MAX_QUERY_FAILURES) {
    stopPolling(watcher);
    applySnapshot(watcher, {
      phase: 'error',
      polling: false,
      retryable: true,
      error: queryError,
    });
    return;
  }
  applySnapshot(watcher, {
    phase: 'querying',
    polling: true,
    retryable: true,
    error: queryError,
  });
  scheduleNext(watcher);
}

function runQuery(watcher: InternalWatcher): void {
  if (watcher.disposed || stopIfIdentityChanged(watcher)) return;
  if (watcher.inFlight) {
    watcher.pendingCheck = true;
    return;
  }
  const generation = watcher.generation;
  watcher.inFlight = true;
  void getOrderByCode(watcher.orderCode)
    .then((status) => {
      if (watcher.disposed || watcher.generation !== generation) return;
      if (stopIfIdentityChanged(watcher)) return;
      applyStatus(watcher, status);
    })
    .catch((error: unknown) => {
      if (watcher.disposed || watcher.generation !== generation) return;
      if (stopIfIdentityChanged(watcher)) return;
      applyQueryError(watcher, error);
    })
    .finally(() => {
      watcher.inFlight = false;
      if (watcher.pendingCheck && !watcher.disposed && watcher.snapshot.polling) {
        watcher.pendingCheck = false;
        runQuery(watcher);
      } else {
        watcher.pendingCheck = false;
      }
    });
}

function isDefiniteTerminal(snapshot: AsaasPaymentSnapshot): boolean {
  return snapshot.phase === 'succeeded' || snapshot.phase === 'unrecoverable';
}

function createWatcher(orderCode: string): AsaasPaymentWatcher {
  const identityKey = getCurrentAsaasIdentityKey();
  const internal: InternalWatcher = {
    orderCode,
    identityKey,
    snapshot: {
      orderCode,
      phase: identityKey ? 'querying' : 'unrecoverable',
      polling: !!identityKey,
      retryable: !!identityKey,
    },
    listeners: new Set(),
    startedAt: Date.now(),
    failures: 0,
    inFlight: false,
    pendingCheck: false,
    generation: 0,
    disposed: false,
  };
  const watcher: AsaasPaymentWatcher = {
    orderCode,
    getSnapshot: () => ({ ...internal.snapshot }),
    subscribe: (listener) => {
      internal.listeners.add(listener);
      return () => internal.listeners.delete(listener);
    },
    refresh: () => {
      if (internal.disposed || isDefiniteTerminal(internal.snapshot)) return;
      if (stopIfIdentityChanged(internal)) return;
      internal.generation += 1;
      internal.startedAt = Date.now();
      internal.failures = 0;
      stopPolling(internal);
      applySnapshot(internal, {
        phase: 'querying',
        polling: true,
        retryable: true,
        error: undefined,
      });
      runQuery(internal);
    },
    requestCheck: () => {
      if (internal.disposed || isDefiniteTerminal(internal.snapshot)) return;
      if (stopIfIdentityChanged(internal)) return;
      stopPolling(internal);
      runQuery(internal);
    },
  };
  internalWatchers.set(orderCode, internal);
  watchers.set(orderCode, watcher);
  if (identityKey) runQuery(internal);
  return watcher;
}

export function ensureAsaasPaymentWatcher(orderCode: string): AsaasPaymentWatcher {
  const existing = internalWatchers.get(orderCode);
  if (existing) {
    if (!identityChanged(existing)) return watchers.get(orderCode)!;
    disposeWatcher(existing);
  }
  return createWatcher(orderCode);
}

export function getAsaasPaymentWatcher(orderCode: string): AsaasPaymentWatcher | undefined {
  return watchers.get(orderCode);
}

export function isAsaasTerminalPhase(phase: AsaasPaymentSnapshot['phase']): boolean {
  return ['succeeded', 'failed', 'cancelled', 'unrecoverable'].includes(phase);
}

export function isAsaasContinuePayBlocked(phase: AsaasPaymentSnapshot['phase']): boolean {
  return ['succeeded', 'cancelled', 'unrecoverable'].includes(phase);
}

export function disposeAsaasPaymentWatchers(): void {
  for (const watcher of [...internalWatchers.values()]) disposeWatcher(watcher);
  panelCounts.clear();
  panelSources.clear();
}

export function resetAsaasPaymentSuccessEffects(): void {
  refreshedOrders.clear();
  announcedOrders.clear();
}
